import type { Event, TelemetryData, Course, EventLikeResponse, PopularEvent } from '../types';
import { getAuthHeaders } from './auth';

const API_BASE = import.meta.env.VITE_API_URL;

export interface TelemetryHistoryResponse {
  [riderId: string]: TelemetryData[];
}

export async function fetchEvents(status?: string): Promise<Event[]> {
  const url = status ? `${API_BASE}/events?status=${status}` : `${API_BASE}/events`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Failed to fetch events');
  }
  return response.json();
}

export async function fetchEvent(id: string): Promise<Event> {
  const response = await fetch(`${API_BASE}/events/${id}`);
  if (!response.ok) {
    throw new Error('Failed to fetch event');
  }
  return response.json();
}

export async function createEvent(data: { name: string; maxRiders?: number; isPublic?: boolean }): Promise<Event> {
  const response = await fetch(`${API_BASE}/events`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error('Failed to create event');
  }
  return response.json();
}

export interface JoinEventRequest {
  eventCode: string;
  deviceId?: string;
  deviceCode?: string;
  nickname?: string;
  force?: boolean;
}

export interface JoinEventSuccessResponse {
  success: true;
  eventId: string;
  riderId: string;
  eventName: string;
}

export interface JoinEventConflictResponse {
  conflict: true;
  existingEvent: {
    id: string;
    name: string;
    code: string;
    status: string;
  };
  message: string;
}

export type JoinEventResponse = JoinEventSuccessResponse | JoinEventConflictResponse;

export async function joinEvent(data: JoinEventRequest): Promise<JoinEventResponse> {
  const response = await fetch(`${API_BASE}/events/join`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to join event' }));
    throw new Error(error.message || 'Failed to join event');
  }
  return response.json();
}

export async function startEvent(id: string) {
  const response = await fetch(`${API_BASE}/events/${id}/start`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error('Failed to start event');
  }
  return response.json();
}

export async function finishEvent(id: string) {
  const response = await fetch(`${API_BASE}/events/${id}/finish`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error('Failed to finish event');
  }
  return response.json();
}

export async function fetchEventTelemetryHistory(eventId: string, limit = 300): Promise<TelemetryHistoryResponse> {
  const response = await fetch(`${API_BASE}/telemetry/event/${eventId}/history?limit=${limit}`);
  if (!response.ok) {
    throw new Error('Failed to fetch telemetry history');
  }
  return response.json();
}

/**
 * Fetch full telemetry history for replay (no limit)
 * Used for replaying finished events
 */
export async function fetchFullEventHistory(eventId: string): Promise<TelemetryHistoryResponse> {
  const response = await fetch(`${API_BASE}/telemetry/event/${eventId}/full-history`);
  if (!response.ok) {
    throw new Error('Failed to fetch full telemetry history');
  }
  return response.json();
}

export async function uploadCourse(eventId: string, gpxFile: File): Promise<{ id: string; course: Course }> {
  const formData = new FormData();
  formData.append('gpx', gpxFile);

  const response = await fetch(`${API_BASE}/events/${eventId}/course`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to upload course' }));
    throw new Error(error.message || 'Failed to upload course');
  }
  return response.json();
}

export async function removeCourse(eventId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/events/${eventId}/course`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to remove course');
  }
}

export interface FitReplayResponse {
  id: string;
  name: string;
  code: string;
  type: 'fit_replay';
  status: 'finished';
  startedAt: number;
  finishedAt: number;
  riderCount: number;
  riders: {
    id: string;
    nickname: string;
    color: string;
  }[];
}

export interface FitPreviewRider {
  index: number;
  name: string;
  color: string;
  coordinates: [number, number][]; // [lng, lat][]
  totalDistance: number; // meters
  duration: number; // seconds
  recordCount: number;
}

export interface FitPreviewResponse {
  previewId: string;
  riders: FitPreviewRider[];
  bounds: {
    minLng: number;
    maxLng: number;
    minLat: number;
    maxLat: number;
  };
}

export interface GpsPoint {
  lat: number;
  lng: number;
}

export interface Segment {
  start: GpsPoint;
  end?: GpsPoint;
  radiusMeters?: number;
}

export type FitReplayMode = 'time_trial' | 'replay';

/**
 * Upload FIT files and get preview data (coordinates for map)
 */
export async function previewFitFiles(
  files: File[],
  riderNames?: string[],
): Promise<FitPreviewResponse> {
  const formData = new FormData();

  for (const file of files) {
    formData.append('fits', file);
  }

  if (riderNames && riderNames.length > 0) {
    formData.append('riderNames', JSON.stringify(riderNames));
  }

  const response = await fetch(`${API_BASE}/fit-replay/preview`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to preview FIT files' }));
    throw new Error(error.message || 'Failed to preview FIT files');
  }

  return response.json();
}

/**
 * Create FIT replay from previewed session with optional segment and GPX course
 */
export async function createFitReplayFromPreview(
  previewId: string,
  name: string,
  riderNames?: string[],
  segment?: Segment,
  mode: FitReplayMode = 'time_trial',
  gpxFile?: File,
): Promise<FitReplayResponse> {
  const formData = new FormData();
  formData.append('previewId', previewId);
  formData.append('name', name);
  formData.append('mode', mode);

  if (riderNames && riderNames.length > 0) {
    formData.append('riderNames', JSON.stringify(riderNames));
  }

  if (segment) {
    formData.append('segment', JSON.stringify(segment));
  }

  if (gpxFile) {
    formData.append('gpx', gpxFile);
  }

  const response = await fetch(`${API_BASE}/fit-replay/create-from-preview`, {
    method: 'POST',
    headers: { ...getAuthHeaders() },  // No Content-Type for FormData
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to create FIT replay' }));
    throw new Error(error.message || 'Failed to create FIT replay');
  }

  return response.json();
}

/**
 * Original function for backward compatibility
 */
export async function createFitReplay(
  name: string,
  files: File[],
  riderNames?: string[],
): Promise<FitReplayResponse> {
  const formData = new FormData();
  formData.append('name', name);

  for (const file of files) {
    formData.append('fits', file);
  }

  if (riderNames && riderNames.length > 0) {
    formData.append('riderNames', JSON.stringify(riderNames));
  }

  const response = await fetch(`${API_BASE}/fit-replay/create`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to create FIT replay' }));
    throw new Error(error.message || 'Failed to create FIT replay');
  }

  return response.json();
}

/**
 * Toggle like on an event (login required)
 */
export async function toggleEventLike(eventId: string): Promise<EventLikeResponse> {
  const response = await fetch(`${API_BASE}/events/${eventId}/like`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to toggle like' }));
    throw new Error(error.message);
  }
  return response.json();
}

/**
 * Get like status for an event
 */
export async function getEventLikeStatus(eventId: string): Promise<EventLikeResponse> {
  const response = await fetch(`${API_BASE}/events/${eventId}/like`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error('Failed to get like status');
  }
  return response.json();
}

/**
 * Fetch popular events (sorted by like count)
 */
export async function fetchPopularEvents(limit = 10): Promise<PopularEvent[]> {
  const response = await fetch(`${API_BASE}/events/popular?limit=${limit}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error('Failed to fetch popular events');
  }
  return response.json();
}

/**
 * Fetch events liked by current user (login required)
 */
export async function fetchLikedEvents(): Promise<PopularEvent[]> {
  const response = await fetch(`${API_BASE}/events/liked`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error('Failed to fetch liked events');
  }
  return response.json();
}

// ============ Chat API ============

export interface ChatMessageResponse {
  id: string;
  eventId: string;
  userId: string;
  nickname: string;
  profileImage?: string;
  badges: string[];
  message: string;
  timestamp: number;
}

/**
 * Fetch chat history for an event
 */
export async function fetchChatHistory(
  eventId: string,
  limit = 50,
  before?: string,
): Promise<ChatMessageResponse[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (before) {
    params.append('before', before);
  }
  const response = await fetch(`${API_BASE}/events/${eventId}/chat?${params}`);
  if (!response.ok) {
    throw new Error('Failed to fetch chat history');
  }
  return response.json();
}

// ============ Share API ============

export interface ShareLinkResponse {
  code: string;
  url: string;
}

/**
 * Create a share link for an event
 * Works for both logged-in and anonymous users
 */
export async function createShareLink(eventId: string): Promise<ShareLinkResponse> {
  const response = await fetch(`${API_BASE}/share/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ eventId }),
  });
  if (!response.ok) {
    throw new Error('Failed to create share link');
  }
  return response.json();
}

export interface ShareStats {
  totalLinks: number;
  totalClicks: number;
  totalConversions: number;
  links: Array<{
    code: string;
    eventId: string;
    eventName?: string;
    clickCount: number;
    conversionCount: number;
    createdAt: string;
  }>;
}

/**
 * Get share stats for current user (login required)
 */
export async function fetchShareStats(): Promise<ShareStats> {
  const response = await fetch(`${API_BASE}/share/stats`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error('Failed to fetch share stats');
  }
  return response.json();
}

// ============ Open Contribution API ============

export interface ContributionInfo {
  eventId: string;
  eventName: string;
  isOpenContribution: boolean;
  contributionMode: FitReplayMode | null;
  contributionSegment: Segment | null;
  maxRiders: number;
  currentRiderCount: number;
  canContribute: boolean;
  alreadyContributed: boolean;
  ownerId: string | null;
  ownerNickname: string | null;
}

export interface RouteValidation {
  isValid: boolean;
  coveragePercent: number;
  onCoursePoints: number;
  totalPoints: number;
  threshold: number;
  minimumRequired: number;
  errorMessage?: string;
}

export interface ContributionPreviewResponse {
  previewId: string;
  riderName: string;
  coordinates: [number, number][];
  totalDistance: number;
  duration: number;
  recordCount: number;
  passesStartPoint: boolean;
  passesEndPoint: boolean;
  startPointIndex?: number;
  endPointIndex?: number;
  routeValidation?: RouteValidation;
}

export interface ContributionResult {
  success: boolean;
  riderId: string;
  riderName: string;
  color: string;
  message?: string;
}

export interface OpenContributionEvent {
  id: string;
  name: string;
  code: string;
  contributionMode: FitReplayMode;
  maxRiders: number;
  currentRiderCount: number;
  spotsRemaining: number;
  locationName: string | null;
  locationNameKo: string | null;
  totalDistance: number | null;
  createdAt: string;
  ownerId: string | null;
  ownerNickname: string | null;
  routePreview: {
    coordinates: [number, number][];
    bounds: {
      minLng: number;
      maxLng: number;
      minLat: number;
      maxLat: number;
    };
  } | null;
}

export interface EnableOpenContributionRequest {
  mode: FitReplayMode;
  segment?: Segment;
  maxRiders?: number;
}

/**
 * Get contribution info for an event
 */
export async function getContributionInfo(eventId: string): Promise<ContributionInfo> {
  const response = await fetch(`${API_BASE}/events/${eventId}/contribution-info`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error('Failed to get contribution info');
  }
  return response.json();
}

/**
 * Enable open contribution for an event (owner only)
 */
export async function enableOpenContribution(
  eventId: string,
  dto: EnableOpenContributionRequest,
): Promise<Event> {
  const response = await fetch(`${API_BASE}/events/${eventId}/open-contribution`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(dto),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to enable open contribution' }));
    throw new Error(error.message);
  }
  return response.json();
}

/**
 * Disable open contribution for an event (owner only)
 */
export async function disableOpenContribution(eventId: string): Promise<Event> {
  const response = await fetch(`${API_BASE}/events/${eventId}/open-contribution`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({}),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to disable open contribution' }));
    throw new Error(error.message);
  }
  return response.json();
}

/**
 * Preview FIT file contribution (validate before submitting)
 */
export async function previewContribution(
  eventId: string,
  file: File,
): Promise<ContributionPreviewResponse> {
  const formData = new FormData();
  formData.append('fit', file);

  const response = await fetch(`${API_BASE}/events/${eventId}/contribute/preview`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to preview contribution' }));
    throw new Error(error.message);
  }
  return response.json();
}

/**
 * Submit contribution from preview
 */
export async function submitContributionFromPreview(
  eventId: string,
  previewId: string,
  riderName?: string,
): Promise<ContributionResult> {
  const response = await fetch(`${API_BASE}/events/${eventId}/contribute/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ previewId, riderName }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to submit contribution' }));
    throw new Error(error.message);
  }
  return response.json();
}

/**
 * Submit contribution directly (without preview)
 */
export async function submitContribution(
  eventId: string,
  file: File,
  riderName?: string,
): Promise<ContributionResult> {
  const formData = new FormData();
  formData.append('fit', file);
  if (riderName) {
    formData.append('riderName', riderName);
  }

  const response = await fetch(`${API_BASE}/events/${eventId}/contribute`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to submit contribution' }));
    throw new Error(error.message);
  }
  return response.json();
}

/**
 * Fetch open contribution events
 */
export async function fetchOpenContributionEvents(
  limit = 20,
  offset = 0,
): Promise<{ events: OpenContributionEvent[]; total: number }> {
  const response = await fetch(
    `${API_BASE}/events/open-contributions?limit=${limit}&offset=${offset}`,
    { headers: getAuthHeaders() },
  );
  if (!response.ok) {
    throw new Error('Failed to fetch open contribution events');
  }
  return response.json();
}
